/**
 * FinancialDataState — truthful presentation state for financial panels
 * (Design System §5)
 *
 * Every financial number on screen has a provenance: when the server
 * calculated it, when this client fetched it, and whether it can currently be
 * trusted. This component renders that provenance instead of a blanket
 * "Live" claim, and gives the non-data states (loading, empty, offline,
 * unavailable, denied) one consistent, safe presentation.
 *
 * Data-bearing states (`fresh`, `refreshing`, `stale`, `offline` with
 * children) render the panel content plus a factual metadata line. Non-data
 * states replace the content with the matching explanation surface.
 */

import React from 'react';
import { t } from '@budgeting101/i18n';
import { EmptyState, EmptyStateProps } from './EmptyState.js';
import { SafeError } from './SafeError.js';
import { PermissionDenied } from './PermissionDenied.js';

export type FinancialDataStatus =
  | 'loading'
  | 'fresh'
  | 'refreshing'
  | 'partial'
  | 'stale'
  | 'offline'
  | 'unavailable'
  | 'empty'
  | 'denied';

export interface FinancialDataMeta {
  /** Server-declared calculation cutoff for the numbers shown (ISO 8601). */
  calculatedAsOf?: string;
  /** When this client last fetched the data successfully (ISO 8601). */
  fetchedAt?: string;
  /** Latest financial effective date included in the numbers (ISO date). */
  effectiveThrough?: string;
}

export interface FinancialDataStateProps {
  status: FinancialDataStatus;
  /** What this panel shows, in the user's terms, e.g. "Cash position". */
  label: string;
  /** Server cutoff and fetch provenance for the visible numbers. */
  meta?: FinancialDataMeta;
  locale?: string;
  /** Retry action for stale/offline/unavailable states. */
  onRetry?: () => void;
  /** Exact verb for the retry action. Defaults to "Refresh". */
  retryLabel?: string;
  /** Whether a retry request is currently in flight. */
  retrying?: boolean;
  /** Why the data may be stale, e.g. "the last refresh failed". */
  staleReason?: string;
  /** Explicit completeness facts when a recoverable page or subset is shown. */
  completeness?: {
    loadedCount: number;
    totalCount?: number;
    message?: string;
  };
  /** Shape-matched skeleton shown while loading, so layout never jumps. */
  loadingSkeleton?: React.ReactNode;
  /** Empty-state copy: explain the absence and offer the next step. */
  empty?: {
    message: string;
    icon?: React.ReactNode;
    action?: EmptyStateProps['action'];
  };
  /** Unavailable-state copy; raw server text must never be passed through. */
  error?: {
    title?: string;
    message?: string;
    referenceId?: string;
  };
  /** Denied-state copy and safe destinations. */
  denied?: {
    requiredPermission?: string;
    explanation?: string;
    actions?: React.ReactNode;
  };
  /** Panel content for the data-bearing states. */
  children?: React.ReactNode;
  className?: string;
}

/** Display metadata only — never ledger math — so Date is acceptable here. */
function formatTimestamp(iso: string | undefined, locale: string): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

function formatDateOnly(iso: string | undefined, locale: string): string | null {
  if (!iso) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day, 12));
  if (
    date.getUTCFullYear() !== year
    || date.getUTCMonth() !== month - 1
    || date.getUTCDate() !== day
  ) return null;
  // An accounting day is not an instant. Format its calendar fields in UTC
  // so a west-of-UTC browser cannot display the previous date.
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone: 'UTC' }).format(date);
}

function metaLine(
  status: FinancialDataStatus,
  meta: FinancialDataMeta | undefined,
  locale: string,
  staleReason?: string,
  completeness?: FinancialDataStateProps['completeness'],
): string | null {
  const cutoff = formatTimestamp(meta?.calculatedAsOf, locale);
  const fetched = formatTimestamp(meta?.fetchedAt, locale);
  const effectiveThrough = formatDateOnly(meta?.effectiveThrough, locale);

  const parts: string[] = [];
  if (status === 'offline') parts.push(t('ui.offline'));
  if (cutoff) parts.push(t('ui.asOf', { date: cutoff }));
  if (effectiveThrough) parts.push(t('ui.includesEntriesThrough', { date: effectiveThrough }));
  if (fetched && fetched !== cutoff) parts.push(t('ui.fetchedTime', { time: fetched }));
  if (status === 'refreshing') parts.push(t('ui.refreshing'));
  if (status === 'stale') parts.push(staleReason ?? t('ui.mayBeOutOfDate'));
  if (status === 'offline') parts.push(t('ui.reconnectToUpdate'));
  if (status === 'partial') {
    parts.push(
      completeness?.message
      ?? (completeness?.totalCount === undefined
        ? t('ui.recordsLoaded', { loaded: completeness?.loadedCount ?? 0 })
        : t('ui.recordsLoadedOfTotal', { loaded: completeness.loadedCount, total: completeness.totalCount })),
    );
  }

  // A fresh panel with no known provenance states nothing rather than
  // fabricating a claim.
  if (parts.length === 0) return null;
  return parts.join(' · ');
}

export function FinancialDataState({
  status,
  label,
  meta,
  locale = 'en-US',
  onRetry,
  retryLabel = 'Refresh',
  retrying = false,
  staleReason,
  completeness,
  loadingSkeleton,
  empty,
  error,
  denied,
  children,
  className,
}: FinancialDataStateProps) {
  const rootClass = [
    'ui-financial-data-state',
    `ui-financial-data-state--${status}`,
    className ?? '',
  ].filter(Boolean).join(' ');

  if (status === 'loading') {
    return (
      <div
        className={rootClass}
        role="status"
        aria-busy="true"
        aria-label={t('ui.loadingLabel', { label })}
        style={loadingSkeleton ? undefined : {
          padding: 'var(--space-6)',
          fontSize: 'var(--text-body-size)',
          color: 'var(--color-text-secondary)',
        }}
      >
        {loadingSkeleton ?? <>{t('ui.loadingLabel', { label })}…</>}
      </div>
    );
  }

  if (status === 'empty') {
    const line = metaLine('fresh', meta, locale, undefined, completeness);
    return (
      <div className={rootClass} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <EmptyState
          icon={empty?.icon}
          message={empty?.message ?? t('ui.nothingRecordedYet', { label })}
          action={empty?.action}
        />
        {line && (
          <div
            className="ui-financial-data-state__meta"
            role="status"
            style={{
              fontSize: 'var(--text-caption-size)',
              lineHeight: 'var(--text-caption-lh)',
              color: 'var(--color-text-secondary)',
            }}
          >
            {line}
          </div>
        )}
      </div>
    );
  }

  if (status === 'unavailable') {
    return (
      <div className={rootClass}>
        <SafeError
          title={error?.title ?? t('ui.couldNotLoadLabel', { label })}
          message={
            error?.message
              ?? t('ui.unavailableLabelMessage', { label })
          }
          referenceId={error?.referenceId}
          onRetry={onRetry}
          retryLabel={retryLabel === 'Refresh' ? t('ui.tryAgain') : retryLabel}
          retrying={retrying}
        />
      </div>
    );
  }

  if (status === 'denied') {
    return (
      <div className={rootClass}>
        <PermissionDenied
          subject={label}
          requiredPermission={denied?.requiredPermission}
          explanation={denied?.explanation}
          actions={denied?.actions}
        />
      </div>
    );
  }

  if (status === 'offline' && !children) {
    return (
      <div className={rootClass}>
        <SafeError
          title={t('ui.offlineLabelUnavailable', { label })}
          message={t('ui.offlineLabelMessage', { label })}
          onRetry={onRetry}
          retryLabel={retryLabel === 'Refresh' ? t('ui.tryAgain') : retryLabel}
          retrying={retrying}
        />
      </div>
    );
  }

  // Data-bearing states: fresh | refreshing | stale | offline (with children)
  const line = metaLine(status, meta, locale, staleReason, completeness);
  const warn = status === 'stale' || status === 'offline' || status === 'partial';

  return (
    <div className={rootClass} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      {children}
      {(line || (warn && onRetry)) && (
        <div
          className="ui-financial-data-state__meta"
          role="status"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 'var(--space-3)',
            fontSize: 'var(--text-caption-size)',
            lineHeight: 'var(--text-caption-lh)',
            color: warn ? 'var(--color-warning)' : 'var(--color-text-secondary)',
          }}
        >
          {line && <span>{line}</span>}
          {warn && onRetry && (
            <button
              type="button"
              className="ui-financial-data-state__retry"
              onClick={onRetry}
              disabled={retrying}
              aria-busy={retrying || undefined}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                font: 'inherit',
                color: 'var(--color-text)',
                textDecoration: 'underline',
              }}
            >
              {retryLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
